import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { uploadTeamLogoToBlob } from "../../../services/api.service.js";
import { useAuth } from "../../auth/use-auth.js";
import { TeamGeneralTab } from "../tabs/team-general-tab.js";
import { TeamLogoDropzone } from "../tabs/theme/components/team-logo-dropzone.js";
import { CreateTeamSubmitError } from "./create-team-submit-error.js";
import { TeamCreateMembersTab } from "./tabs/team-create-members-tab.js";
import type { SelectedMember } from "./tabs/team-create-members-tab.js";
import { TeamCreateThemeTab } from "./team-create-theme-tab.js";
import type { useCreateTeamDialog } from "./use-create-team-dialog.js";

const PAID_WIZARD_STEPS = ["General", "Members", "Theme"] as const;
type WizardStep = (typeof PAID_WIZARD_STEPS)[number];

interface ThemeDraft {
  selectedPresetId: string | null;
  selectedLogoFile: File | null;
  selectedLogoPreviewUrl: string | null;
}

interface CreateTeamWizardState {
  step: WizardStep;
  highestUnlockedStepIndex: number;

  name: string;
  addAllOrganizationUsersToTeam: boolean;
  selectedMembers: SelectedMember[];

  themeDraft: ThemeDraft;
  uploadErrorMessage: string | null;
  submitNameErrorMessage: string | null;
}

export type { ThemeDraft };

const getStepIndex = (steps: readonly WizardStep[], step: WizardStep) =>
  steps.indexOf(step);

const createInitialState = (): CreateTeamWizardState => ({
  addAllOrganizationUsersToTeam: false,
  highestUnlockedStepIndex: 0,
  name: "",
  selectedMembers: [],
  step: "General",
  submitNameErrorMessage: null,
  themeDraft: {
    selectedLogoFile: null,
    selectedLogoPreviewUrl: null,
    selectedPresetId: null,
  },
  uploadErrorMessage: null,
});

interface TeamCreateFlowProps {
  isStarter: boolean;
  submitAfterGeneral: boolean;
  wizardSteps: readonly WizardStep[];
  props: ReturnType<typeof useCreateTeamDialog>["props"];
}

export const TeamCreateFlow = ({
  isStarter,
  submitAfterGeneral,
  wizardSteps,
  props,
}: TeamCreateFlowProps) => {
  const { user } = useAuth();
  const [state, setState] = useState<CreateTeamWizardState>(createInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const submitting = props.loading || isSubmitting;

  useEffect(() => {
    if (props.open) {
      // Reset the local wizard whenever the dialog opens for a new office.
      // eslint-disable-next-line react/set-state-in-effect
      setState(createInitialState());
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  }, [props.open]);

  const selectedStepIndex = getStepIndex(wizardSteps, state.step);

  const steps = useMemo(
    () =>
      wizardSteps.map((step) => ({
        index: getStepIndex(wizardSteps, step),
        step,
        unlocked:
          getStepIndex(wizardSteps, step) <= state.highestUnlockedStepIndex,
      })),
    [state.highestUnlockedStepIndex, wizardSteps]
  );

  const goToStep = (step: WizardStep) => {
    if (getStepIndex(wizardSteps, step) > state.highestUnlockedStepIndex) {
      return;
    }

    setState((prev) => ({ ...prev, step }));
  };

  const advanceStep = (nextStep: WizardStep) => {
    setState((prev) => ({
      ...prev,
      highestUnlockedStepIndex: Math.max(
        prev.highestUnlockedStepIndex,
        getStepIndex(wizardSteps, nextStep)
      ),
      step: nextStep,
    }));
  };

  const handleCreate = (
    currentState: CreateTeamWizardState,
    theme: {
      presetId?: string;
      uploadToken: string | null;
    }
  ) => {
    if (!currentState.name.trim()) {
      return {
        error: new CreateTeamSubmitError({
          message: "Office name is required",
        }),
        ok: false as const,
      };
    }

    const memberIdsExcludingCurrentUser = currentState.selectedMembers
      .filter((member) => member.msUserId !== user.id)
      .map((member) => member.msUserId);

    return props.handleCreateTeam({
      addAllOrganizationUsersToTeam: currentState.addAllOrganizationUsersToTeam,
      name: currentState.name.trim(),
      ...(theme.presetId ? { presetId: theme.presetId } : {}),
      selectedMemberMsUserIds: memberIdsExcludingCurrentUser,
      type: "regular",
      ...(theme.uploadToken ? { uploadToken: theme.uploadToken } : {}),
    });
  };

  const setThemeDraft = useCallback((draft: Partial<ThemeDraft>) => {
    setState((prev) => ({
      ...prev,
      themeDraft: { ...prev.themeDraft, ...draft },
    }));
  }, []);
  const handleContinue222 = useStableCallback(() => advanceStep("Theme"));

  const handleBack221 = useStableCallback(() => goToStep("General"));

  const handleAddAllOrganizationUsers195 = useStableCallback(() => {
    setState((prev) => ({
      ...prev,
      addAllOrganizationUsersToTeam: true,
      selectedMembers: [],
    }));
  });
  const handleRemoveMember229 = useStableCallback((member: SelectedMember) => {
    if (member.msUserId === user.id) {
      return;
    }

    setState((prev) => ({
      ...prev,
      selectedMembers: prev.selectedMembers.filter(
        (entry) => entry.msUserId !== member.msUserId
      ),
    }));
  });

  const handleAddMembers208 = useStableCallback((members: SelectedMember[]) => {
    setState((prev) => {
      const existingIds = new Set(prev.selectedMembers.map((m) => m.msUserId));
      const newMembers = members.filter((m) => !existingIds.has(m.msUserId));

      return {
        ...prev,
        addAllOrganizationUsersToTeam: false,
        selectedMembers: [...prev.selectedMembers, ...newMembers],
      };
    });
  });

  const handleOpenChange154 = useStableCallback((_event, data) => {
    if (!data.open) {
      props.setOpen(false);
    }
  });

  const handleUploadErrorMessageChange = useStableCallback((message) => {
    setState((prev) => ({ ...prev, uploadErrorMessage: message }));
  });

  const uploadSelectedLogo = async () => {
    if (!state.themeDraft.selectedLogoFile) {
      return null;
    }

    return await uploadTeamLogoToBlob(state.themeDraft.selectedLogoFile);
  };

  const submitCreateTeam = async ({
    presetId,
    uploadToken,
    currentState = state,
  }: {
    presetId?: string;
    uploadToken: string | null;
    currentState?: CreateTeamWizardState;
  }) => {
    let finalUploadToken = uploadToken;

    try {
      finalUploadToken ??= await uploadSelectedLogo();
    } catch {
      handleUploadErrorMessageChange(
        "Failed to upload office logo. Please try again."
      );
      return;
    }

    const result = await handleCreate(
      currentState,
      presetId
        ? { presetId, uploadToken: finalUploadToken }
        : { uploadToken: finalUploadToken }
    );

    if (result.ok) {
      return;
    }

    const nameError = result.error.fieldErrors.name;

    if (nameError) {
      setState((prev) => ({
        ...prev,
        step: "General",
        submitNameErrorMessage: nameError,
      }));
      return;
    }

    if (result.error.code === "starter-office-limit-reached") {
      props.setOpen(false);
    }
  };

  const runSubmit = async (submit: () => Promise<void>) => {
    if (submitInFlightRef.current || props.loading) {
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      await submit();
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit246 = useStableCallback(
    async ({ presetId, uploadToken }) => {
      await runSubmit(async () => {
        await submitCreateTeam({
          presetId,
          uploadToken,
        });
      });
    }
  );

  const handleSelectedLogoFileChange = useStableCallback(
    (file: File | null) => {
      setThemeDraft({ selectedLogoFile: file });

      if (!file) {
        setThemeDraft({ selectedLogoPreviewUrl: null });
      }
    }
  );

  const handleLogoDelete = useStableCallback(() => {
    setThemeDraft({
      selectedLogoFile: null,
      selectedLogoPreviewUrl: null,
    });
  });

  const handleLogoPreviewUrlChange = useStableCallback((url) => {
    setThemeDraft({ selectedLogoPreviewUrl: url });
  });

  const submitWithDefaultPreset = async (
    currentState: CreateTeamWizardState = state
  ) => {
    await runSubmit(async () => {
      try {
        await submitCreateTeam({
          currentState,
          uploadToken: null,
        });
      } catch {
        handleUploadErrorMessageChange(
          "Failed to create office. Please try again."
        );
      }
    });
  };

  const handleSubmitName189 = useStableCallback(({ name }) => {
    setState((prev) => ({
      ...prev,
      name,
      submitNameErrorMessage: null,
    }));

    if (submitAfterGeneral) {
      void submitWithDefaultPreset({ ...state, name });
      return;
    }

    advanceStep(wizardSteps.includes("Members") ? "Members" : "Theme");
  });

  const handleCreateFromMembers = useStableCallback(() => {
    submitWithDefaultPreset();
  });

  return (
    <Dialog onOpenChange={handleOpenChange154} open={props.open}>
      <DialogSurface className="relative sm:max-h-[90lvh] sm:w-[90lvw] md:min-w-[900px] lg:min-w-[1000px]">
        <DialogBody className="flex flex-col sm:max-h-[90lvh]">
          <DialogTitle className="text-bzd-gray-400">
            Create new office
          </DialogTitle>
          <DialogContent className="flex flex-1 flex-col overflow-hidden lg:flex-row">
            <div className="border-bzd-border-100 w-full border-b p-4 lg:h-full lg:w-[200px] lg:flex-shrink-0 lg:border-r lg:border-b-0">
              <ul className="flex flex-row gap-6 lg:flex-col lg:gap-2">
                {steps.map((step) => (
                  <li key={step.step}>
                    <button
                      className={`hover:border-bzd-gray-500 border-b-4 pb-2 hover:cursor-pointer lg:border-b-0 lg:border-l-4 lg:pl-4 ${selectedStepIndex === step.index ? "border-bzd-gray-500" : "border-transparent"} ${step.unlocked ? "" : "cursor-not-allowed opacity-40 hover:border-transparent"}`}
                      disabled={!step.unlocked}
                      onClick={asStableCallback(() => goToStep(step.step))}
                      type="button"
                    >
                      {step.step}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-6 md:max-h-[600px] md:min-h-[600px]">
              {state.step === "General" && (
                <TeamGeneralTab
                  externalValidationMessage={state.submitNameErrorMessage}
                  initialName={state.name}
                  onSubmitName={handleSubmitName189}
                  submitLabel="Continue"
                >
                  <TeamLogoDropzone
                    disabled={submitting}
                    errorMessage={state.uploadErrorMessage}
                    onDeleteChange={handleLogoDelete}
                    onErrorMessageChange={handleUploadErrorMessageChange}
                    onLogoPreviewUrlChange={handleLogoPreviewUrlChange}
                    onSelectedFileChange={handleSelectedLogoFileChange}
                    previewUrl={state.themeDraft.selectedLogoPreviewUrl}
                  />
                </TeamGeneralTab>
              )}

              {state.step === "Members" && (
                <TeamCreateMembersTab
                  continueLabel={isStarter ? "Create office" : "Continue"}
                  currentUserDisplayName={user.name}
                  currentUserEmail={user.email}
                  currentUserId={user.id}
                  members={state.selectedMembers}
                  onAddAllOrganizationUsers={handleAddAllOrganizationUsers195}
                  onAddMembers={handleAddMembers208}
                  onBack={handleBack221}
                  onContinue={
                    isStarter ? handleCreateFromMembers : handleContinue222
                  }
                  onRemoveMember={handleRemoveMember229}
                  submitting={submitting}
                />
              )}

              {state.step === "Theme" && (
                <TeamCreateThemeTab
                  onSubmit={handleSubmit246}
                  setThemeDraft={setThemeDraft}
                  submitting={submitting}
                  themeDraft={state.themeDraft}
                />
              )}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
